import {useEffect} from 'react'
import axios from "axios"
import { useState } from "react"
import InterceptorModal from "./InterceptorModal"
import { useAppDispatch } from "../../app/hooks"
import {openLogin} from '../../activeScreenSlice'
import { useAuth } from '../../components/AuthContext/AuthContext';
import isTokenValid from '../../util/getTokenValidity';
import { useQueryClient } from '@tanstack/react-query';
import { showToast } from "../../toastSlice"

export interface Props {
  children: any
}

export default function Interceptor(props: Props) {
  const [showModal, setShowModal] = useState(false)
  const { authToken, setAuthToken } = useAuth();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  axios.interceptors.request.use(config => {
    if (localStorage.getItem("expiration") && !isTokenValid()) {
      //Token expired
      localStorage.removeItem("expiration");
      localStorage.removeItem("ssoEnabled");
      
      setAuthToken("");
      dispatch(openLogin());

      queryClient.cancelQueries();
      queryClient.removeQueries();
      showToast("Access token expired.")
      // Throw error to cancel request
      return Promise.reject(new Error('Token expired'));
    }

    if (config.url &&
    (config.url.includes('/users/refresh_token') || 
    config.url.includes('/users/validate-forgot-password') ||
    config.url.includes('/users/login') ||
    config.url.includes('/users/logout') ||
    config.url.includes('/users/login-with-temp-password') ||
    config.url.includes('/users/sso/token')))
    {
      config.withCredentials = true;
    }

    if(config.url && (config.url.includes('/settings/download_opco_template') || config.url.includes('/settings/download_from_gcs'))){
      config.responseType = 'blob';
    }
    if(config.url && config.url.includes('/users/template_download')){
      config.responseType = 'blob';
    }

    return config;
  });

  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      config => {
        // If token exists then add it to the config
        if (localStorage.getItem("expiration") !== null && authToken.length > 0) {
          config.headers['Authorization'] = `Bearer ${authToken}`;
        }
        return config;
      },
      error => {
        // Ensure the rejection reason is an Error object
        if (!(error instanceof Error)) {
          error = new Error('Request failed');
        }
        return Promise.reject(error);
      }
    );

    // Cleanup
    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, [authToken]);

  // Add a response interceptor
  axios.interceptors.response.use(
  (response) => {
    // If the request was successful, return the response
    return response;
  },
  (error) => {
    // Handle unauthorized errors (401)
    if (error.response && error.response.status === 401 && !showModal && authToken !== "") {
      // Show a login modal or navigate to a login page
      setShowModal(true);

        // Uncomment and implement if needed
        // dispatch(openLogin());
        // queryClient.setDefaultOptions({
        //   queries: {
        //     retry: true,
        //     retryDelay: 30000,
        //   },
        // }); // Slow down retries to every 30 seconds
      } else if (authToken === "") {
        // If there's no auth token, hide the modal
        setShowModal(false);
      }
      
      // Ensure the rejection reason is an Error object
      if (!(error instanceof Error)) {
        error = new Error(error.message || 'An unknown error occurred');
      }

      // Reject the promise with the error
      return Promise.reject(error);
    }
  );

  return (
    <>
      <InterceptorModal
        open={showModal}
        handleClose={() => setShowModal(false)}
      />
      {props.children}
    </>
  )
}
